import {Component, ElementRef, Inject, OnInit, ViewEncapsulation} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {PasswordService} from '../../gen/api/password.service';
import {ChangePasswordRequest} from '../../gen/model/changePasswordRequest';
import {User} from '../../gen/model/user';
import {AppConstants} from '../AppConstants';
import {StandaloneComponent} from '../standalone.component';
import {AlertHandler} from '../no-concurrent-alerts-handler';
import {cardFooterFeedbackTransition} from '../animations';
import {Observable} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {ConfigurationService} from '../ui-configuration/configuration-service';
import {AppStateService} from '../app-state.service';
import {Credential, PasswordCredentialInfo } from '../../gen';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    cardFooterFeedbackTransition
  ],
})
export class ChangePasswordComponent extends StandaloneComponent implements OnInit {

  profile: User;

  changePasswordForm: FormGroup;
  confirmChange = false;

  submitFailed = false;
  authorizationError:  { error: string, error_description: string }|undefined;
  invalidNewPWD = false;
  rejectedNewPWD = false;
  isSetPassword = false;

  constructor(@Inject('PasswordApi') private passwordApi: PasswordService,
              private appStateService: AppStateService,
              private alertHandler: AlertHandler,
              private translate: TranslateService,
              route: ActivatedRoute,
              router: Router,
              hostElement: ElementRef,
              configuration: ConfigurationService) {
    super(route, router, hostElement, configuration);
    this.changePasswordForm = new FormGroup({
      newPassword: new FormControl('', [Validators.required, Validators.pattern(configuration.getProperties().passwordValidation)]),
      confirmNewPassword: new FormControl('', [Validators.required]),
    });

    this.closeConfirm = this.closeConfirm.bind(this);
    this.submit = this.submit.bind(this);
    // Note: does not look like much, but without this binding, the component will happily render incorrect crap without any errors.
    // Angular 101, how to make even the most trivial things complicated and require special knowledge
    this.onPasswordLoaded = this.onPasswordLoaded.bind(this);
  }

  ngOnInit() {
    super.init();
    this.validatePasswords = this.validatePasswords.bind(this);
    this.changePasswordForm.setValidators([this.validatePasswords]);
    this.passwordApi.listPasswordCredentialInfo().subscribe(this.onPasswordLoaded, (err) => {
      this.onPasswordLoaded(undefined);
    });
  }
  private onPasswordLoaded(body: PasswordCredentialInfo[]|undefined) {
    if (!!body && body.length) {
      this.isSetPassword = false;
    } else {
      this.isSetPassword = true;
    }
  }
  getProfile(): User {
    return this.appStateService.getAppState().getAuthenticationState().getProfile();
  }

  isFieldInvalid(field: AbstractControl): boolean {
    return this.hasError(field) || this.isInputInvalid(field);
  }

  isInputInvalid(field: AbstractControl): boolean {
    if (field === this.changePasswordForm.get('confirmNewPassword')) {
      return (field.invalid ||
        (this.changePasswordForm.invalid && this.changePasswordForm.errors && this.changePasswordForm.errors.mustMatch)) && field.touched;

    } else {
      return field.invalid && field.touched;
    }
  }

  isFieldValid(field: AbstractControl): boolean {
    return !this.hasError(field) && this.isInputValid(field);
  }

  isInputValid(field: AbstractControl): boolean {
    if (field === this.changePasswordForm.get('confirmNewPassword')) {
      return field.valid
        && (this.changePasswordForm.valid || !this.changePasswordForm.errors || !this.changePasswordForm.errors.mustMatch)
        && field.touched;
    } else {
      return field.valid && field.touched;
    }
  }

  hasError(field?: AbstractControl, code?: string): boolean {
    let retVal = false;
    if (!field) {
      retVal = this.submitFailed;
    } else if (field === this.changePasswordForm.get('newPassword')) {
      retVal = this.invalidNewPWD || this.rejectedNewPWD;
    }
    return retVal;
  }
  confirm() {
    this.confirmChange = true;
  }
  closeConfirm() {
    this.confirmChange = false;
  }
  submit(cred: Credential) {
    this.submitFailed = false;
    this.authorizationError = undefined;
    this.invalidNewPWD = false;
    this.rejectedNewPWD = false;
    const obj = {
      credentials: [cred],
      newPassword: this.changePasswordForm.get('newPassword').value,
    } as ChangePasswordRequest;
    this.passwordApi.changePassword(obj, 'response').subscribe((response) => {
      this.router.navigate([AppConstants.PATH_EDIT_PROFILE], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        fragment: 'login',
      });
    }, (err) => {
      this.submitFailed = true;
      if (err && err.error &&
        (err.error.error === 'invalid_username_or_password') ||
          err.error.error === 'invalid_credential') {
        this.authorizationError = err.error;
      } else if (err && err.error && err.error.error === 'password_quality_not_satisfied') {
        this.invalidNewPWD = true;
      } else if (err && err.error && err.error.error === 'password_update_denied') {
        this.rejectedNewPWD = true;
      }
    });
  }

  private validatePasswords(control: AbstractControl): ValidationErrors | null {
    const pwField = this.changePasswordForm.get('newPassword');
    const cpwField = this.changePasswordForm.get('confirmNewPassword');
    return cpwField.value === pwField.value
      ? null
      : {'mustMatch': true};
  }
  public resolveWindowTitlePart(): Observable<string|undefined> {
    return this.isSetPassword ? this.translate.get('change-password.set-password-window-title') : this.translate.get('change-password.window-title');
  }

}
